import { useRef, useState } from "react";
import { Camera, Image as ImageIcon, FolderOpen } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import DocScanner from "@/components/shared/DocScanner";

// 184: Nahrání dokladu na JEDNO klepnutí — „Nahrát fakturu / účtenku" rovnou
// otevře výběr zdroje, žádná naše mezistránka „Odkud nahrát?".
//   iPhone/iPad → nativní nabídka iOS (Knihovna fotek / Pořídit obrázek / Vybrat soubor),
//   Android     → stejná nabídka od nás (Vyfotit / Vybrat z galerie / Vybrat soubor) —
//                 Chrome na Androidu (Xiaomi, Samsung) u `image/*` ukáže jen galerii
//                 bez foťáku, foťák jen s `capture` (viz PhotoPicker),
//   počítač     → rovnou okno pro výběr souboru.
// Klik na skrytý <input> musí proběhnout přímo v obsluze klepnutí (iOS jinak
// výběr zablokuje) — proto `accept` nastavujeme imperativně těsně před click().
const IMG_EXT = ".jpg,.jpeg,.png,.webp,.heic,.heif";
const ACCEPT = {
  faktura: { native: "image/*,application/pdf", file: `.pdf,application/pdf,${IMG_EXT}` },
  uctenka: { native: "image/*", file: IMG_EXT },
};
// 203: tlačítko „PDF“ ve skeneru — jen PDF, bez obrázků: iPhone pak rovnou otevře
// Soubory (bez nabídky Knihovna fotek / Pořídit obrázek / Vybrat soubor), Android správce souborů
const SCAN_PDF = "application/pdf,.pdf";
const TITLE = { faktura: "Nahrát fakturu", uctenka: "Nahrát účtenku" };

export const isAndroid = () => typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
// Telefon (198): úzký displej a dotyk → skener s kamerou. Počítač (i úzké okno) ne.
export const isPhone = () => {
  try { return !!window.matchMedia && window.matchMedia("(max-width: 767px) and (pointer: coarse)").matches; } catch { return false; }
};

export function useDocPicker({ onPick, disabled = false } = {}) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const fileRef = useRef(null);
  const typeRef = useRef("faktura");
  const [menu, setMenu] = useState(null);   // typ dokladu, pro který je otevřená nabídka (Android)
  const [scan, setScan] = useState(null);   // telefon: { type, choose } otevřeného skeneru

  const onChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";                   // stejný soubor půjde vybrat znovu
    if (f) { setScan(null); onPick?.(f, typeRef.current); }
  };
  const click = (ref, accept) => {
    if (!ref.current) return;
    ref.current.accept = accept;
    ref.current.click();
  };
  const fromMenu = (src) => {
    const t = typeRef.current;
    setMenu(null);
    if (src === "camera") click(camRef, "image/*");
    else if (src === "gallery") click(galRef, "image/*");
    else click(fileRef, ACCEPT[t].file);
  };

  // choose: ve skeneru jde přepnout Faktura / Účtenka (horní lišta, účetní)
  const open = (type = "faktura", { choose = false } = {}) => {
    if (disabled) return;
    typeRef.current = ACCEPT[type] ? type : "faktura";
    // telefon: skener s kamerou; o tik později, ať se nejdřív zavře menu / šuplík
    if (isPhone()) { const t = typeRef.current; setTimeout(() => setScan({ type: t, choose }), 0); return; }
    // Android: naše nabídka; o tik později, ať se nejdřív zavře menu / šuplík,
    // ze kterého se otevírá (Radix jinak nechá stránku zablokovanou).
    if (isAndroid()) setTimeout(() => setMenu(typeRef.current), 0);
    else click(galRef, ACCEPT[typeRef.current].native);
  };

  const inputs = (
    <>
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onChange} className="hidden" data-testid="docpick-camera" />
      <input ref={galRef} type="file" accept="image/*" onChange={onChange} className="hidden" data-testid="docpick-input" />
      <input ref={fileRef} type="file" accept={IMG_EXT} onChange={onChange} className="hidden" data-testid="docpick-file" />
      {/* 201: skener je vlastní nejvyšší vrstva Radixu — otevřený z jiného okna (např.
          „Nahrát fakturu“) by jinak nedostal klepnutí (Radix dá body pointer-events:none)
          a první klepnutí by spodní okno zavřelo a smazalo */}
      {scan && (
        <DialogPrimitive.Root open onOpenChange={(o) => { if (!o) setScan(null); }}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Content aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()} className="fixed inset-0 z-[70] outline-none" data-testid="doc-scanner-layer">
              <DialogPrimitive.Title className="sr-only">{scan.type === "uctenka" ? "Naskenovat účtenku" : "Naskenovat fakturu"}</DialogPrimitive.Title>
              <DocScanner type={scan.type} choose={scan.choose}
                onType={(t) => { typeRef.current = t; setScan((s) => (s ? { ...s, type: t } : s)); }}
                onClose={() => setScan(null)}
                onShot={(file) => { const t = typeRef.current; setScan(null); onPick?.(file, t); }}
                onCamera={() => click(camRef, "image/*")}
                onGallery={() => click(galRef, "image/*")}
                onFile={() => click(fileRef, typeRef.current === "faktura" ? SCAN_PDF : ACCEPT[typeRef.current].file)} />
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}
      <Dialog open={!!menu} onOpenChange={(o) => !o && setMenu(null)}>
        <DialogContent sheet hideClose className="max-w-sm p-3 gap-1 max-md:pt-8" data-testid="docpick-sheet">
          <DialogTitle className="text-center text-[13px] font-semibold text-muted-foreground pb-2">{TITLE[menu] || "Nahrát doklad"}</DialogTitle>
          {[
            [Camera, "Vyfotit", "camera"],
            [ImageIcon, "Vybrat z galerie", "gallery"],
            [FolderOpen, menu === "faktura" ? "Vybrat soubor (PDF)" : "Vybrat soubor", "file"],
          ].map(([Icon, label, src]) => (
            <button key={src} type="button" onClick={() => fromMenu(src)} data-testid={`docpick-${src}-btn`}
              className="w-full flex items-center gap-3 h-14 px-3 rounded-[14px] text-[15px] font-semibold hover:bg-secondary active:bg-secondary transition-colors">
              <span className="w-9 h-9 rounded-full grid place-items-center bg-brand/10 text-brand shrink-0"><Icon size={18} /></span>
              {label}
            </button>
          ))}
          <button type="button" onClick={() => setMenu(null)} data-testid="docpick-cancel"
            className="mt-1 w-full h-12 rounded-[14px] bg-secondary font-bold">Zrušit</button>
        </DialogContent>
      </Dialog>
    </>
  );

  return { inputs, open };
}
